import { withSource } from "~/actions/context";
import {
  type Action,
  type ActionContext,
  actionChildren,
  isVisible,
  type MenuItem,
  resolve,
} from "~/actions/types";

export const SEPARATOR_PREFIX = "separator";

/** A menu divider expressed as an action, so a definition file can lay out its own menu. */
export function separator(key = "separator"): Action {
  return { id: `${SEPARATOR_PREFIX}.${key}`, name: "", section: "" };
}

function isSeparator(action: Action): boolean {
  return action.id === SEPARATOR_PREFIX || action.id.startsWith(`${SEPARATOR_PREFIX}.`);
}

/**
 * Dividers are a layout hint, not content. Once invisible actions have been dropped a
 * divider can end up leading, trailing, or doubled — none of which should ever render.
 */
function collapseSeparators(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    if (item.type === "separator") {
      const previous = out[out.length - 1];
      if (!previous || previous.type === "separator" || previous.type === "heading") {
        continue;
      }
    }
    out.push(item);
  }
  while (out.length > 0 && out[out.length - 1]?.type === "separator") {
    out.pop();
  }
  return out;
}

/**
 * Turns actions into menu rows. Anything that can neither be performed nor opened is not a
 * menu row — dropping it here is what keeps empty submenus from ever appearing.
 */
export function actionsToMenuItems(actions: readonly Action[], context: ActionContext): MenuItem[] {
  const ctx = withSource(context, "contextmenu");
  const items: MenuItem[] = [];

  for (const action of actions) {
    if (isSeparator(action)) {
      items.push({ type: "separator" });
      continue;
    }
    if (!isVisible(action, ctx)) {
      continue;
    }

    const title = resolve(action.name, ctx);
    const children = actionChildren(action, ctx);

    if (children.length > 0) {
      const nested = actionsToMenuItems(children, ctx);
      if (nested.length === 0) {
        continue;
      }
      items.push({
        type: "submenu",
        title,
        ...(action.icon ? { icon: action.icon } : {}),
        items: nested,
      });
      continue;
    }

    if (!action.perform) {
      continue;
    }

    items.push({
      type: "button",
      title,
      ...(action.icon ? { icon: action.icon } : {}),
      ...(action.selected?.(ctx) ? { selected: true } : {}),
      ...(action.dangerous ? { dangerous: true } : {}),
      onClick: () => {
        void action.perform?.(ctx);
      },
    });
  }

  return collapseSeparators(items);
}
